import {
  type GameStartRequestBody,
  type GameType,
  getBalltrackerByMachineUuidUnifiedQueryKey,
  getMachinesSearchQueryKey,
  getMachinesSearchTaggingQueryKey,
  type ParkConfig,
  postBalltrackerByMachineUuidBlocked,
  postBalltrackerByMachineUuidStatTracking,
  postBalltrackerByMachineUuidUmpireAssist,
  postBalltrackerByMachineUuidUnified,
  postGameByGameIdEnd,
  postGameStart,
} from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { SCHEDULES_QUERY_KEY } from "../Complexes/SingleField/FieldSchedule/FieldSchedule.queries";

type LedMode = "blocked" | "stat_tracking" | "umpire_assist";

const LED_FN_BY_MODE = {
  blocked: postBalltrackerByMachineUuidBlocked,
  stat_tracking: postBalltrackerByMachineUuidStatTracking,
  umpire_assist: postBalltrackerByMachineUuidUmpireAssist,
} as const;

export type StartLiveEventInput = {
  machineUuid: string;
  parkConfig: ParkConfig;
  // `null` represents the "Standby" choice — no LED endpoint is dispatched and
  // the LED board is left in its default state.
  ledMode: LedMode | null;
  ledEndTimeS: number;
  orgId: string | null;
  gameBody: GameStartRequestBody;
};

// Sequenced live-start orchestration: park config → LED activation → game start →
// game endTime. game.times[].endTime is only writable on an existing game, so it
// has to follow /game/start. No rollback on partial failure — park/LED writes are
// idempotent and re-submitting overwrites them.
export const useStartLiveEvent = ({
  onSuccess,
}: {
  onSuccess?: (machineUuid: string, gameType: GameType | undefined) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: StartLiveEventInput) => {
      await postBalltrackerByMachineUuidUnified({
        path: { machine_uuid: input.machineUuid },
        body: input.parkConfig,
        throwOnError: true,
      });
      // `ledMode: null` = Standby — skip the LED dispatch
      // entirely so the board stays in its default state.
      const extra = input.orgId ? { orgId: input.orgId } : undefined;
      const ledEndTime = new Date(input.ledEndTimeS * 1000).toISOString();
      if (input.ledMode === "umpire_assist") {
        // Order matters: the backend rejects /umpire_assist with
        // "No current stat-tracking feature available to update into
        // umpire-assist" when no stat_tracking entitlement exists. POST
        // /stat_tracking first, then layer /umpire_assist on top. Same
        // sequencing as the machine-update flow in useUpdateMachineLedMode.
        await postBalltrackerByMachineUuidStatTracking({
          path: { machine_uuid: input.machineUuid },
          body: { endTime: ledEndTime, ...(extra && { extra }) },
          throwOnError: true,
        });
        await postBalltrackerByMachineUuidUmpireAssist({
          path: { machine_uuid: input.machineUuid },
          body: { endTime: ledEndTime, ...(extra && { extra }) },
          throwOnError: true,
        });
      } else if (input.ledMode) {
        await LED_FN_BY_MODE[input.ledMode]({
          path: { machine_uuid: input.machineUuid },
          body: { endTime: ledEndTime, ...(extra && { extra }) },
          throwOnError: true,
        });
      }
      const { data } = await postGameStart({
        body: input.gameBody,
        throwOnError: true,
      });
      const gameUuid = data?.game?.gameUuid;
      if (gameUuid) {
        await postGameByGameIdEnd({
          path: { game_id: gameUuid },
          body: { endTime: new Date(input.ledEndTimeS * 1000).toISOString() },
          throwOnError: true,
        });
      }
      return { machineUuid: input.machineUuid, data };
    },
    onSuccess: async ({ machineUuid }, input) => {
      // Live events show up via machineField.currentGame on SingleField,
      // SingleComplex, etc. Invalidate the machines queries so the new
      // game appears without a manual refresh. /balltracker/{uuid}/unified
      // is also invalidated so EditEvent (which reads field via useField)
      // sees the new currentGame on first navigation — otherwise its stale
      // cache misses the gameUuid match and falls through to a 404 schedule
      // fetch.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getMachinesSearchQueryKey({}),
        }),
        queryClient.invalidateQueries({
          queryKey: getMachinesSearchTaggingQueryKey({}),
        }),
        queryClient.invalidateQueries({
          queryKey: getBalltrackerByMachineUuidUnifiedQueryKey({
            path: { machine_uuid: machineUuid },
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: [SCHEDULES_QUERY_KEY],
          exact: false,
          refetchType: "all",
        }),
      ]);
      onSuccess?.(machineUuid, input.gameBody.type);
    },
  });
};
